import { UIKit } from "@/shared/ui";
/**
 * Виджет «Свойства» — правая панель редактора.
 *
 * Показывает параметры выделенного объекта (стена / проём / угол / прибор / светильник /
 * кабель) и позволяет задать их числом, а не мышью: для чертежа это основной способ ввода.
 * У выключателя здесь видно, какие светильники он зажигает, у светильника — какой
 * выключатель его включает: группа освещения читается из проводки, а не хранится.
 * Не хранит паспорт проекта и экспликацию: это отдельное меню верхней панели.
 */
import { detectRooms } from "@/widgets/plan";
import { type CatalogItem } from "@/shared/catalog";
import { useFloorsStore } from "@/shared/store/layers";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { formatArea } from "@/shared/lib/geometry";
import { CatalogSectionKind } from "@/shared/plan";
import { CATALOG, allowedFloorLayouts, floorLook, getFloorLayout } from "@/shared/catalog";
import { RoomCeilingProperties } from "./RoomCeilingProperties";
import { RoomBaseboardProperties } from "./RoomBaseboardProperties";

export type PropertiesPanelCatalogItem = CatalogItem;

export const RoomProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const graph = layers.walls;
	const selection = useEditorStore((s) => s.selection);
	const activeCatalog = useEditorStore((s) => s.activeCatalog);
	const movingFloorPatternRoomId = useEditorStore((s) => s.movingFloorPatternRoomId);
	const setMovingFloorPatternRoomId = useEditorStore((s) => s.setMovingFloorPatternRoomId);
	const setRoomFloorMaterial = useFloorsStore((s) => s.setRoomFloorMaterial);
	const removeRoomFloor = useFloorsStore((s) => s.removeRoomFloor);

	// Хелпер для updateFixture в зависимости от каталога

	const rooms = detectRooms(graph);

	if (!selection) return null;
	if (selection.kind === SelectionKind.Room) {
		const room = rooms.find((item) => item.id === selection.id) ?? null;
		if (!room) return null;

		if (activeCatalog === CatalogSectionKind.Baseboard && layers.baseboard.items[room.id]) {
			return <RoomBaseboardProperties room={room} />;
		}

		// Включён слой потолков — на плане поверх пола лежит потолок, его и настраиваем
		if (layers.ceiling.visible) {
			return (
				<>
					<RoomCeilingProperties room={room} />
				</>
			);
		}

		//TODO тут баг починить отображение для гидроизоляции и заливки пола
		// Показывать настройки пола только если слой Полы видимый
		if (!layers.floors.visible) {
			return null;
		}

		// Покрытие не назначено — панель не выдумывает его: показывает только выбор материала,
		// иначе на холсте голая заливка, а в панели «плитка 600 × 600», которой нет в модели
		const roomFloor = layers.floors.items[room.id];
		const current = roomFloor?.material;
		// Что показать в полях: настройки комнаты поверх типовых из каталога
		const look = floorLook(roomFloor);
		const currentLayout = look.layout;
		const currentLength = look.length;
		const currentWidth = look.width;
		const currentGap = look.gap;
		const currentTileColor = look.color;
		const currentSeamColor = look.seamColor;
		const currentRaise = look.raise;
		const currentRotation = look.rotation;
		// Укладки, которые каталог разрешает этому покрытию; одна — выбирать нечего
		const layouts = current === undefined ? [] : allowedFloorLayouts(current);
		return (
			<>
				<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
					Пол комнаты
				</UIKit.Text>
				<div className="rounded-lg bg-slate-50 px-2.5 py-2 text-xs text-ink">
					Площадь {formatArea(room.area)} • {room.nodeIds.length} углов
				</div>
				{!roomFloor && (
					<UIKit.Text className="text-xs leading-copy text-amber-800">
						Покрытие не назначено — выберите материал, и комната получит его с типовыми настройками.
					</UIKit.Text>
				)}
				<div className="flex flex-col gap-1.5">
					{CATALOG.floor.items.map((material) => {
						const active = current === material.material;
						return (
							<UIKit.Button
								key={material.id}
								type="button"
								className={`flex items-center justify-between rounded-lg border px-2.5 py-2 text-left text-sm transition-colors ${
									active
										? "border-accent bg-accent-soft text-accent"
										: "border-border bg-surface text-ink hover:border-slate-300"
								}`}
								onClick={() => setRoomFloorMaterial(room.id, material.material, currentLayout)}
							>
								<UIKit.Text as="span" className="flex items-center gap-2">
									<UIKit.Text
										as="span"
										className="size-2.5 rounded-sm"
										style={{ background: material.color }}
									/>
									{material.name}
								</UIKit.Text>
								{active ? "выбран" : ""}
							</UIKit.Button>
						);
					})}
				</div>
				{roomFloor && current !== undefined && (
					<div className="flex flex-col gap-1.5">
						<UIKit.IconButton
							icon={<UIKit.IconMove />}
							label="Двигать раскладку"
							labelPosition="right"
							active={movingFloorPatternRoomId === room.id}
							tooltip={{ label: "Потяните рисунок пола на плане" }}
							className="h-9 border border-border px-2"
							onClick={() =>
								setMovingFloorPatternRoomId(movingFloorPatternRoomId === room.id ? null : room.id)
							}
						/>
						<UIKit.Text
							as="span"
							className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
						>
							{layouts.length > 1 ? "Укладка" : "Плитка"}
						</UIKit.Text>
						{layouts.length > 1 &&
							layouts.map((layout) => (
								<UIKit.Button
									key={layout.id}
									type="button"
									className={`rounded-lg border px-2.5 py-2 text-left text-sm ${
										currentLayout === layout.id
											? "border-accent bg-accent-soft text-accent"
											: "border-border bg-surface text-ink hover:border-slate-300"
									}`}
									onClick={() =>
										setRoomFloorMaterial(room.id, current, layout.id, {
											length: currentLength,
											width: currentWidth,
											gap: currentGap,
											tileColor: currentTileColor,
											seamColor: currentSeamColor,
											raise: currentRaise,
											rotation: currentRotation,
										})
									}
								>
									{getFloorLayout(layout.id).label}
								</UIKit.Button>
							))}
						<div className="mt-1 flex flex-col gap-2">
							<UIKit.NumberField
								label="Длина плитки"
								value={currentLength}
								min={50}
								step={25}
								onChange={(length) =>
									setRoomFloorMaterial(room.id, current, currentLayout, {
										length,
										width: currentWidth,
										gap: currentGap,
										tileColor: currentTileColor,
										seamColor: currentSeamColor,
										raise: currentRaise,
										rotation: currentRotation,
									})
								}
							/>
							<UIKit.NumberField
								label="Ширина плитки"
								value={currentWidth}
								min={50}
								step={25}
								onChange={(width) =>
									setRoomFloorMaterial(room.id, current, currentLayout, {
										length: currentLength,
										width,
										gap: currentGap,
										tileColor: currentTileColor,
										seamColor: currentSeamColor,
										raise: currentRaise,
										rotation: currentRotation,
									})
								}
							/>
							<label className="flex flex-col gap-1 text-xs text-ink">
								Цвет плитки
								<UIKit.ColorPicker
									label=""
									value={currentTileColor}
									onChange={(color) =>
										setRoomFloorMaterial(room.id, current, currentLayout, {
											length: currentLength,
											width: currentWidth,
											gap: currentGap,
											tileColor: color,
											seamColor: currentSeamColor,
											raise: currentRaise,
											rotation: currentRotation,
										})
									}
								/>
							</label>
							<label className="flex flex-col gap-1 text-xs text-ink">
								Цвет шва
								<UIKit.ColorPicker
									label=""
									value={currentSeamColor}
									onChange={(color) =>
										setRoomFloorMaterial(room.id, current, currentLayout, {
											length: currentLength,
											width: currentWidth,
											gap: currentGap,
											tileColor: currentTileColor,
											seamColor: color,
											raise: currentRaise,
											rotation: currentRotation,
										})
									}
								/>
							</label>
							<UIKit.NumberField
								label="Ширина шва"
								value={currentGap}
								min={0}
								max={30}
								step={1}
								onChange={(gap) =>
									setRoomFloorMaterial(room.id, current, currentLayout, {
										length: currentLength,
										width: currentWidth,
										gap,
										tileColor: currentTileColor,
										seamColor: currentSeamColor,
										raise: currentRaise,
										rotation: currentRotation,
									})
								}
							/>
							<UIKit.NumberField
								label="Подъем пола"
								value={currentRaise}
								min={0}
								step={10}
								onChange={(raise) =>
									setRoomFloorMaterial(room.id, current, currentLayout, {
										length: currentLength,
										width: currentWidth,
										gap: currentGap,
										tileColor: currentTileColor,
										seamColor: currentSeamColor,
										raise,
										rotation: currentRotation,
									})
								}
							/>
							<UIKit.NumberField
								label="Угол поворота"
								unit="°"
								value={currentRotation}
								min={0}
								max={359}
								step={5}
								onChange={(rotation) =>
									setRoomFloorMaterial(room.id, current, currentLayout, {
										length: currentLength,
										width: currentWidth,
										gap: currentGap,
										tileColor: currentTileColor,
										seamColor: currentSeamColor,
										raise: currentRaise,
										rotation,
									})
								}
							/>
						</div>
					</div>
				)}
				{roomFloor && (
					<UIKit.Button
						type="button"
						className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-50"
						onClick={() => removeRoomFloor(room.id)}
					>
						Удалить покрытие
					</UIKit.Button>
				)}
			</>
		);
	}

	return null;
};

/** Список типов раздела каталога — сменить тип объекта, не переставляя его. */
export function CatalogTypeList({
	items,
	current,
	onPick,
}: {
	items: CatalogItem[];
	current: string;
	onPick: (itemId: string) => void;
}) {
	if (items.length === 0) return null;
	return (
		<>
			<UIKit.Text
				as="span"
				className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
			>
				Тип
			</UIKit.Text>
			<div className="flex flex-col gap-1">
				{items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						className={`flex items-center gap-2 rounded-lg border border-transparent px-2.25 py-1.75 text-left text-sm text-ink hover:bg-[#f3f5f9]${
							current === item.id ? " border-accent bg-accent-soft text-accent" : ""
						}`}
						onClick={() => onPick(item.id)}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						<UIKit.Text as="span" className="min-w-0 flex-1">
							{item.name}
						</UIKit.Text>
					</UIKit.Button>
				))}
			</div>
		</>
	);
}
